/* Flash deal products use the shared BF cart and discount controller. */
(() => {
  if (customElements.get('bf-flash') || !window.BFOfferElement) return;
  class BFFlash extends window.BFOfferElement {
    connectedCallback() {
      super.connectedCallback();
      this.track = this.querySelector('[data-track]');
      this.previous = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      const { signal } = this.lifecycle;
      this.previous.addEventListener('click', () => this.move(-1), { signal });
      this.next.addEventListener('click', () => this.move(1), { signal });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') this.scroll(event.key === 'Home' ? 0 : this.track.scrollWidth);
        else this.move(event.key === 'ArrowLeft' ? -1 : 1);
      }, { signal });
      document.addEventListener('shopify:block:select', (event) => {
        const item = [...this.track.children].find((child) => child.dataset.blockId === event.detail?.blockId);
        if (item) this.scroll(item.offsetLeft - this.track.firstElementChild.offsetLeft, true);
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.layout());
      this.resizeObserver.observe(this.track);
      this.layout();
      this.addEventListener('bf:flash:inventory', (event) => {
        if (event.target === this) this.updateInventory(event.detail);
      }, { signal });
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-quota-state="sold-out"] [data-flash-purchase]')) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { signal, capture: true });
      this.clearInventory();
      this.dispatchInventoryRequest();
      this.inventoryInterval = setInterval(() => { if (!document.hidden) this.requestInventory(); }, 60000);
      document.addEventListener('cart:change', () => this.requestInventory(), { signal });
      document.addEventListener('cart:refresh', () => this.requestInventory(), { signal });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.requestInventory(); }, { signal });
      window.addEventListener('pageshow', () => this.requestInventory(), { signal });
    }

    layout() {
      const fits = this.track.scrollWidth <= this.track.clientWidth + 2;
      this.previous.hidden = fits;
      this.next.hidden = fits;
    }

    requestInventory() {
      this.refreshShopifyInventory();
      this.dispatchInventoryRequest();
    }

    dispatchInventoryRequest() {
      if (!this.dataset.campaignId) return;
      this.dispatchEvent(new CustomEvent('bf:flash:inventory:request', {
        bubbles: true,
        detail: { version: 1, campaignId: this.dataset.campaignId, productIds: [...new Set([...this.querySelectorAll('[data-product-id]')].map((card) => card.dataset.productId))] }
      }));
    }

    // Read fresh Liquid inventory; the product JSON endpoint omits inventory quantities.
    async refreshShopifyInventory() {
      if (!this.dataset.sectionId || this.inventoryRequest || !this.isConnected) return;
      const controller = new AbortController();
      this.inventoryRequest = controller;
      const abort = () => controller.abort();
      const lifecycle = this.lifecycle.signal;
      lifecycle.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 10000);
      try {
        const url = new URL(window.location.href);
        url.hash = '';
        url.searchParams.delete('sections');
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = [...html.querySelectorAll('bf-flash')].find((element) => element.dataset.sectionId === this.dataset.sectionId);
        if (!fresh || !this.isConnected || controller.signal.aborted) return;
        for (const card of this.querySelectorAll('[data-product-id]')) {
          const updated = [...fresh.querySelectorAll('[data-product-id]')].find((item) => item.dataset.quotaKey === card.dataset.quotaKey && item.dataset.productId === card.dataset.productId);
          if (!updated) continue;
          card.dataset.quotaTotal = updated.dataset.quotaTotal;
          card.dataset.quotaRemaining = updated.dataset.quotaRemaining;
          if (!(this.quotaExpires > Date.now())) this.restoreShopifyInventory(card);
        }
      } catch { /* Keep the last server-rendered state on a transient connection failure. */ }
      finally {
        clearTimeout(timeout);
        lifecycle.removeEventListener('abort', abort);
        if (this.inventoryRequest === controller) this.inventoryRequest = null;
      }
    }

    renderInventory(card, total, remaining) {
      const soldOut = remaining === 0;
      card.dataset.quotaState = soldOut ? 'sold-out' : 'active';
      const bar = card.querySelector('[data-quota-bar]');
      bar.setAttribute('aria-valuenow', String(remaining));
      bar.setAttribute('aria-valuemax', String(total));
      bar.style.setProperty('--progress', String(remaining / total));
      card.querySelector('[data-quota-label]').textContent = this.dataset.remainingLabel.replaceAll('[remaining]', String(remaining)).replaceAll('[total]', String(total));
      card.querySelector('[data-quota]').hidden = this.dataset.showProgress !== 'true';
      card.querySelector('[data-quota-actions]').hidden = soldOut;
      card.querySelector('[data-quota-sold-out]').hidden = !soldOut;
      const purchase = card.querySelector('[data-flash-purchase]');
      if (soldOut) purchase?.setAttribute('aria-disabled', 'true');
      else purchase?.removeAttribute('aria-disabled');
    }

    restoreShopifyInventory(card) {
      const total = Number(card.dataset.quotaTotal);
      const remaining = Number(card.dataset.quotaRemaining);
      if (card.dataset.quotaRemaining !== '' && Number.isSafeInteger(total) && total > 0 && Number.isSafeInteger(remaining) && remaining >= 0 && remaining <= total) {
        this.renderInventory(card, total, remaining);
        return;
      }
      card.dataset.quotaState = 'unconnected';
      card.querySelector('[data-quota]').hidden = true;
      card.querySelector('[data-quota-sold-out]').hidden = true;
      card.querySelector('[data-quota-actions]').hidden = false;
      card.querySelector('[data-flash-purchase]')?.removeAttribute('aria-disabled');
      const bar = card.querySelector('[data-quota-bar]');
      bar.removeAttribute('aria-valuenow');
      bar.removeAttribute('aria-valuemax');
      bar.style.removeProperty('--progress');
      card.querySelector('[data-quota-label]').textContent = '';
    }

    // A display snapshot only. The future backend must enforce the actual cap.
    updateInventory(snapshot) {
      if (!this.dataset.campaignId || snapshot?.version !== 1 || snapshot.campaignId !== this.dataset.campaignId || !Array.isArray(snapshot.items)) return false;
      const updated = Date.parse(snapshot.updatedAt);
      const expires = Date.parse(snapshot.expiresAt);
      const now = Date.now();
      if (!Number.isFinite(updated) || !Number.isFinite(expires) || updated > now + 5000 || updated < (this.quotaUpdated || 0) || expires <= now || expires <= updated || expires - updated > 300000) return false;
      const entries = new Map();
      for (const item of snapshot.items) {
        if (typeof item?.productId !== 'string' || !/^\d+$/.test(item.productId) || entries.has(item.productId) || !Number.isSafeInteger(item.total) || item.total <= 0 || !Number.isSafeInteger(item.remaining) || item.remaining < 0 || item.remaining > item.total) return false;
        entries.set(item.productId, item);
      }
      this.clearInventory();
      this.quotaUpdated = updated;
      this.quotaExpires = expires;
      for (const card of this.querySelectorAll('[data-product-id]')) {
        const item = entries.get(card.dataset.productId);
        if (!item) continue;
        this.renderInventory(card, item.total, item.remaining);
      }
      this.quotaTimer = setTimeout(() => { this.clearInventory(); this.requestInventory(); }, expires - now);
      return true;
    }

    clearInventory() {
      clearTimeout(this.quotaTimer);
      this.quotaExpires = 0;
      for (const card of this.querySelectorAll('[data-product-id]')) {
        this.restoreShopifyInventory(card);
      }
    }

    async add(form) {
      if (form.closest('[data-quota-state="sold-out"]')) { this.notify(this.dataset.soldOutLabel, 'info'); return; }
      await super.add(form);
      if (this.isConnected) this.requestInventory();
    }

    scroll(left, instant = false) {
      this.track.scrollTo({ left, behavior: instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }

    move(direction) {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (max <= 2) return;
      const first = this.track.firstElementChild;
      const step = first.getBoundingClientRect().width + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
      const position = this.track.scrollLeft;
      const next = direction > 0 && position >= max - 2 ? 0 : direction < 0 && position <= 2 ? max : Math.max(0, Math.min(max, position + direction * step));
      this.scroll(next);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      clearInterval(this.inventoryInterval);
      this.inventoryRequest?.abort();
      this.clearInventory();
      this.quotaUpdated = 0;
      super.disconnectedCallback();
    }
  }
  customElements.define('bf-flash', BFFlash);
})();
